import React, { useEffect, useRef, useState } from 'react'
import { StyleSheet } from 'react-native'
import { generateClient } from 'aws-amplify/api'
import { DNABox, DNAButton, Iffy, DNAModal, DNASpinner, DNAText } from '@alucio/lux-ui'
import { VeevaVaultLoginOutput } from '@alucio/aws-beacon-amplify/src/API'
import { loginVeevaVault } from '@alucio/aws-beacon-amplify/src/graphql/mutations'
import InputComponent from 'src/components/Publishers/InputComponent'
import * as logger from 'src/utils/logger'
import { useUserTenant } from 'src/state/redux/selector/user';
import { useAppSettings } from 'src/state/context/AppSettings'
import { useActor } from '@xstate/react';
import { persistVeevaVaultSession } from 'src/state/machines/CRM/util';
import { useHistory } from 'src/router';
import { ROUTES } from 'src/router/routeDef';
import useFeatureFlags from 'src/hooks/useFeatureFlags/useFeatureFlags';

const styles = StyleSheet.create({
  body: {
    paddingHorizontal: 12,
    paddingVertical: 12,
    width: 400,
  },
  loadingContainer: {
    height: 184,
  },
})

interface DNAVeevaVaultLoginProps {
  closeModal: () => void
}

const DNAVeevaVaultLogin: React.FC<DNAVeevaVaultLoginProps> = (props) => {
  const { closeModal } = props;
  const [username, setUsername] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const backToIdleOnClose = useRef<boolean>(true);
  const [error, setError] = useState<string>('');
  const [isConnecting, setIsConnecting] = useState<boolean>(false);
  const tenant = useUserTenant();
  const integrationName = tenant?.config?.crmIntegration?.name;
  const hasExternalOidcSso = Boolean(tenant?.config?.crmIntegration?.externalOidcSso);
  const isExternalOidcSsoFeatureEnabled = useFeatureFlags('BEAC_8226_external_oidc_vault_crm_sso');
  const { crmMachine } = useAppSettings();
  const [, send] = useActor(crmMachine);
  const history = useHistory();

  const onConnect = (veevaVaultUserSession: VeevaVaultLoginOutput) => {
    persistVeevaVaultSession(veevaVaultUserSession);
  };

  const handleConnect = async () => {
    setError('');
    setIsConnecting(true);
    try {
      logger.CRMMachine.info('Attempting Veeva Vault login...');
      const appsyncClient = generateClient();
      const { data } = await appsyncClient.graphql({
        query: loginVeevaVault,
        variables: {
          input: {
            username,
            password,
          },
        },
      });

      logger.CRMMachine.info('Veeva Vault login successful...');
      backToIdleOnClose.current = false;
      onConnect(data.loginVeevaVault);
      closeModal?.();
    } catch (err) {
      const errorMessage = 'Authentication failed';
      logger.CRMMachine.error('Veeva Vault login exception', { error: err })
      setError(errorMessage)
    } finally {
      setIsConnecting(false)
    }
  }

  const isConnectDisabled = !username || !password || isConnecting;

  const handleExternalOidcConnect = () => {
    closeModal?.();
    history.push(ROUTES.SSO_OIDC_START.PATH);
  };

  useEffect(() => {
    return () => {
      if (backToIdleOnClose.current) {
        send({ type: 'BACK_TO_IDLE' });
      }
    };
  }, []);

  return (
    <DNAModal>
      <DNAModal.Body>
        <DNABox appearance="col" spacing="sm" fill style={styles.body}>
          <Iffy is={isConnecting}>
            <DNABox spacing="lg" appearance="col" fill alignX="center" style={styles.loadingContainer}>
              <DNASpinner />
              <DNAText>Connecting...</DNAText>
            </DNABox>
          </Iffy>
          <Iffy is={!isConnecting}>
            {
              integrationName &&
                <DNAText h4 testID="veeva-vault-login-modal-header-text">{integrationName} Login</DNAText>
            }
            <InputComponent
              placeHolder="Username"
              onChangeText={setUsername}
              removeMarginPadding={true}
              testID="veeva-username-input"
              value={username}
              autoFocus={true}
              required={false}
              autoCapitalize="none"
            />
            <InputComponent
              isPassword={true}
              placeHolder="Password"
              onChangeText={setPassword}
              onSubmitEditing={isConnectDisabled ? undefined : handleConnect}
              removeMarginPadding={true}
              testID="veeva-password-input"
              required={false}
              value={password}
            />
            {
              error &&
                <DNABox fill>
                  <DNAText c1 status="danger" >{error}</DNAText>
                </DNABox>
            }
            <DNABox spacing="md" alignX="end">
              <DNAButton
                appearance="filled"
                status="primary"
                onPress={handleConnect}
                disabled={isConnectDisabled}
                testID="veeva-connect-button"
              >
                Connect
              </DNAButton>
              <Iffy is={hasExternalOidcSso && isExternalOidcSsoFeatureEnabled}>
                <DNAButton
                  appearance="ghost"
                  status="primary"
                  onPress={handleExternalOidcConnect}
                  testID="veeva-oidc-connect-button"
                >
                  Connect with Single Sign-On
                </DNAButton>
              </Iffy>
              <DNAButton
                appearance="ghost"
                status="secondary"
                onPress={closeModal}
                testID="veeva-cancel-button"
              >
                Cancel
              </DNAButton>
            </DNABox>
          </Iffy>
        </DNABox>
      </DNAModal.Body>
    </DNAModal>
  )
}

export default DNAVeevaVaultLogin;
